import { type MemoryBackfillReleaseResponse } from '@libraries/typescript/api_client';

// The server's own per-request maximum.
export const RELEASE_ITEM_LIMIT = 500;

export type MemoryBackfillReleaseSummary = {
    readonly tone: 'success' | 'warning' | 'info';
    readonly message: string;
};

/** A selection releases only in part when chronology holds some of it. */
export function buildMemoryBackfillReleaseSummary(
    response: MemoryBackfillReleaseResponse,
): MemoryBackfillReleaseSummary {
    const plural = (count: number, noun: string): string =>
        `${count} ${count === 1 ? noun : `${noun}s`}`;
    const held = response.remaining_parked_item_count;
    // Older work blocks two ways: an unfinished note for the caregiver, or
    // an unprocessed item in the same domain. Releasing it is not enough.
    const heldSentence =
        `${plural(held, 'item')} stayed parked. ` +
        'Earlier notes for the same caregiver have to finish first.';
    const failed = response.failed_publish_item_ids.length;
    const failedSentence =
        `${plural(failed, 'item')} could not be queued yet. ` +
        `The pending sweep will pick ${failed === 1 ? 'it' : 'them'} up.`;

    if (response.released_item_count === 0) {
        return {
            tone: held === 0 ? 'info' : 'warning',
            message:
                held === 0
                    ? 'Nothing left to release.'
                    : `Nothing was released. ${heldSentence}`,
        };
    }
    const released =
        `Released ${plural(response.released_item_count, 'item')} across ` +
        `${plural(response.affected_note_count, 'note')}.`;
    const sentences = [
        released,
        held > 0 ? heldSentence : null,
        failed > 0 ? failedSentence : null,
    ].filter((sentence) => sentence !== null);
    return {
        tone: held > 0 || failed > 0 ? 'warning' : 'success',
        message: sentences.join(' '),
    };
}
